import { useState, useMemo } from 'react';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { type IntegrationDefinition, type IntegrationConnection, type IntegrationCategory, type AccessFinancialsConfig } from '@/types/integrations';
import { CATEGORY_LABELS } from '@/lib/integrations-data';
import { IntegrationCard } from '@/components/integrations/integration-card';
import { StandardConnectionDialog } from '@/components/integrations/standard-connection-dialog';
import { OAuthConnectionDialog } from '@/components/integrations/oauth-connection-dialog';
import { AccessFinancialsDialog } from '@/components/integrations/access-financials-dialog';
import { IntegrationSettingsDialog } from '@/components/integrations/integration-settings-dialog';
import { DisconnectConfirmationDialog } from '@/components/integrations/disconnect-confirmation-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  useIntegrationsWithConnections,
  useCreateConnection,
  useDeleteConnection,
  buildConfigJson,
} from '@/hooks/use-integrations';

export function IntegrationsPage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory>('financial');

  // Dialog states
  const [connectingIntegration, setConnectingIntegration] = useState<IntegrationDefinition | null>(null);
  const [connectingBackendId, setConnectingBackendId] = useState<number>(0);
  const [configuringIntegration, setConfiguringIntegration] = useState<IntegrationDefinition | null>(null);
  const [configuringConnection, setConfiguringConnection] = useState<IntegrationConnection | null>(null);
  const [disconnectingIntegration, setDisconnectingIntegration] = useState<IntegrationDefinition | null>(null);
  const [disconnectingConnection, setDisconnectingConnection] = useState<IntegrationConnection | null>(null);
  const [disconnectingBackendIntegrationId, setDisconnectingBackendIntegrationId] = useState<number>(0);

  const { data: integrationsWithConnections, isLoading } = useIntegrationsWithConnections();
  const createConnection = useCreateConnection();
  const deleteConnection = useDeleteConnection();

  // Hidden categories (for future consideration)
  const hiddenCategories: IntegrationCategory[] = ['credit', 'banking', 'data'];

  // Visible categories for tabs
  const visibleCategories = useMemo(() => {
    return Object.entries(CATEGORY_LABELS).filter(([key]) => !hiddenCategories.includes(key as IntegrationCategory));
  }, []);

  // Group integrations by category
  const integrationsByCategory = useMemo(() => {
    const categories: Record<IntegrationCategory, typeof integrationsWithConnections> = {
      financial: [],
      communication: [],
      credit: [],
      banking: [],
      data: [],
    };

    integrationsWithConnections.forEach(item => {
      const cat = item.definition.category;
      categories[cat].push(item);
    });

    return categories;
  }, [integrationsWithConnections]);

  const handleConnect = (integration: IntegrationDefinition, backendId: number) => {
    setConnectingIntegration(integration);
    setConnectingBackendId(backendId);
  };

  const handleStandardConnect = async (_integrationId: string, credentials: Record<string, string>) => {
    await createConnection.mutateAsync({
      backendIntegrationId: connectingBackendId,
      configJson: buildConfigJson(credentials),
    });
    toast.success('Integration connected successfully!');
  };

  const handleOAuthConnect = async (_integrationId: string, account: string) => {
    await createConnection.mutateAsync({
      backendIntegrationId: connectingBackendId,
      connectedAccount: account,
    });
    toast.success('Integration connected successfully!');
  };

  const handleAccessFinancialsConnect = async (_integrationId: string, config: AccessFinancialsConfig) => {
    await createConnection.mutateAsync({
      backendIntegrationId: connectingBackendId,
      configJson: buildConfigJson(config),
    });
    toast.success('Access Financials connected successfully!');
  };

  const handleConfigure = (integration: IntegrationDefinition, connection: IntegrationConnection) => {
    setConfiguringIntegration(integration);
    setConfiguringConnection(connection);
  };

  const handleReconnect = (integration: IntegrationDefinition) => {
    const item = integrationsWithConnections.find(i => i.definition.id === integration.id);
    setConfiguringIntegration(null);
    setConfiguringConnection(null);
    setConnectingIntegration(integration);
    setConnectingBackendId(item?.backendId ?? 0);
  };

  const handleDisconnect = (connection: IntegrationConnection) => {
    const item = integrationsWithConnections.find(
      i => i.connection?.id === connection.id
    );
    if (item) {
      setDisconnectingIntegration(item.definition);
      setDisconnectingConnection(connection);
      setDisconnectingBackendIntegrationId(item.backendId);
    }
    setConfiguringIntegration(null);
    setConfiguringConnection(null);
  };

  const handleDisconnectConfirm = async (connectionId: string, keepData: boolean) => {
    await deleteConnection.mutateAsync({
      backendIntegrationId: disconnectingBackendIntegrationId,
      connectionId,
    });
    toast.success(
      keepData
        ? 'Integration disconnected. Historical data preserved.'
        : 'Integration disconnected. All data removed.'
    );
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">System Integrations</h1>
        </div>
        <p className="text-muted-foreground mt-2">
          Connect and manage third-party integrations for your organization
        </p>
      </div>

      {/* Integration Categories Tabs */}
      <Tabs value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as IntegrationCategory)}>
        <TabsList className="grid w-full grid-cols-2">
          {visibleCategories.map(([key, label]) => (
            <TabsTrigger key={key} value={key}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(integrationsByCategory)
          .filter(([category]) => !hiddenCategories.includes(category as IntegrationCategory))
          .map(([category, items]) => (
            <TabsContent key={category} value={category} className="space-y-4">
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading integrations…</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(({ definition, backendId, connection }) => (
                    <IntegrationCard
                      key={definition.id}
                      integration={definition}
                      connection={connection}
                      onConnect={() => handleConnect(definition, backendId)}
                      onConfigure={(integration, conn) => handleConfigure(integration, conn)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
      </Tabs>

      {/* Connection Dialogs */}
      {connectingIntegration?.id === 'access-financials' ? (
        <AccessFinancialsDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleAccessFinancialsConnect}
        />
      ) : connectingIntegration?.requiresOAuth ? (
        <OAuthConnectionDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleOAuthConnect}
        />
      ) : (
        <StandardConnectionDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleStandardConnect}
        />
      )}

      {/* Settings Dialog */}
      <IntegrationSettingsDialog
        integration={configuringIntegration}
        connection={configuringConnection}
        open={!!configuringIntegration && !!configuringConnection}
        onClose={() => {
          setConfiguringIntegration(null);
          setConfiguringConnection(null);
        }}
        onReconnect={handleReconnect}
        onDisconnect={handleDisconnect}
      />

      {/* Disconnect Confirmation Dialog */}
      <DisconnectConfirmationDialog
        integration={disconnectingIntegration}
        connection={disconnectingConnection}
        open={!!disconnectingIntegration && !!disconnectingConnection}
        onClose={() => {
          setDisconnectingIntegration(null);
          setDisconnectingConnection(null);
        }}
        onConfirm={handleDisconnectConfirm}
      />
    </div>
  );
}
